import bbox from "@turf/bbox";
import { checkValidGeojsonAndSupportedType, wktToGeoJSON } from "./mapSearchUtils";
/**
 * Compute a bounding box for the given secondary selection.
 *
 * @param secondaryIdsByLayer - Mapping of layerId → array of feature ids that are selected.
 * @param mapLibreLayers - Array of mapLibre layer specifications containing GeoJSON data.
 * @returns The bbox array [minX, minY, maxX, maxY] or null if no matching features.
 */
export const computeSelectionBbox = (
  secondaryIdsByLayer: Record<string, string[]>,
  mapLibreLayers: Record<string, any>[]
): number[] | null => {
  // Gather all matching features across layers using a Set for O(1) look‑ups
  const allFeatures = Object.entries(secondaryIdsByLayer).flatMap(([layerId, featureIds]) => {
    const layer = mapLibreLayers?.find((l) => l.id === layerId);
    if (!layer) return [];
    const idSet = new Set(featureIds);
    // Fulcrum layers use fulcrum_id as the selection key; Django layers use id
    const selectionKey: string = layer.metadata?.isFulcrum ? "fulcrum_id" : "id";
    return layer?.source?.data?.features?.filter((feature: Record<string, any>) => {
      const featureId = feature?.properties?.[selectionKey] ?? feature?.id ?? feature?.properties?.id;
      return idSet.has(featureId);
    }) ?? [];
  });

  if (!allFeatures.length) return null;

  // Wrap the flat array into a FeatureCollection for bbox calculation
  return bbox({ type: "FeatureCollection", features: allFeatures });
};

export const getBboxByGeom = (geometry: any) => {
  if (!geometry) return;
  let result = null;
  try {
    let geoJson;
    if (checkValidGeojsonAndSupportedType(geometry)) {
      geoJson = geometry;
    } else {
      geoJson = wktToGeoJSON(geometry);
    }
    result = bbox(geoJson);
  } catch {
    console.log("Error while generating the bbox");
  }
  return result;
};

/**
 * Helper to check if the outer bbox (prefetched source's bbox) fully contains the inner bbox.
 * Bbox format: [minLng, minLat, maxLng, maxLat]
 */
export const isBboxContained = (outer: number[], inner: number[]): boolean => {
  return (
    outer[0] <= inner[0] &&
    outer[1] <= inner[1] &&
    outer[2] >= inner[2] &&
    outer[3] >= inner[3]
  );
};
